/**
 * Ask a signed-in account with no name or no email for them, once per visit, before
 * it sends its first chat message or posts a listing. See ContactCapturePopup.
 *
 * `askFirst(surface, proceed)` runs `proceed` at once when nothing is missing or the
 * popup already showed for that surface this visit. Otherwise it opens the popup,
 * and "Not now" or "Continue" closes it and runs `proceed`. Closing it with the X
 * does not: the message stays in the box (or the listing on the form), and the next
 * tap goes straight through.
 */
import { useCallback } from 'react';
import { useUI, useUser } from '../../contexts';
import { needsEmailCapture } from '../../utils/email-verification';
import { needsNameCapture } from '../../utils/person-name';
import ContactCapturePopup, { type ContactCaptureSurface } from './ContactCapturePopup';

const KEY = (surface: ContactCaptureSurface) => `sw-contact-ask:${surface}`;
/** For when sessionStorage throws (private windows, blocked storage). */
const askedThisVisit = new Set<ContactCaptureSurface>();

function alreadyAsked(surface: ContactCaptureSurface): boolean {
  if (askedThisVisit.has(surface)) return true;
  try {
    return window.sessionStorage.getItem(KEY(surface)) === '1';
  } catch {
    // Blocked storage reads as "not asked"; the in-memory set still stops a repeat.
    return false;
  }
}

function markAsked(surface: ContactCaptureSurface): void {
  askedThisVisit.add(surface);
  try {
    window.sessionStorage.setItem(KEY(surface), '1');
  } catch {
    // Blocked storage: the in-memory set above covers this page's life.
  }
}

/** Test seam: forget what this visit asked. */
export function resetContactCaptureAsks(): void {
  askedThisVisit.clear();
}

export function useContactCaptureAsk() {
  const { profile, loggedIn } = useUser();
  const { setPopupContents, setPopupOpen } = useUI();

  return useCallback(
    (surface: ContactCaptureSurface, proceed: () => void) => {
      const askName = needsNameCapture(loggedIn, profile);
      const askEmail = needsEmailCapture(loggedIn, profile);
      if ((!askName && !askEmail) || alreadyAsked(surface)) {
        proceed();
        return;
      }
      markAsked(surface);
      setPopupContents(
        <ContactCapturePopup
          surface={surface}
          askName={askName}
          askEmail={askEmail}
          onContinue={() => {
            setPopupOpen(false);
            proceed();
          }}
        />
      );
      setPopupOpen(true);
    },
    [loggedIn, profile, setPopupContents, setPopupOpen]
  );
}
